import React, { useEffect, useState } from "react";
import { JobPostFormData } from "../../../schemas/jobPostValidation";
import {
  List,
  Typography,
  Tag,
  Spin,
  Space,
  Descriptions,
  Divider,
} from "antd";
import {
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  UsergroupAddOutlined,
  CheckOutlined,
  QuestionCircleOutlined,
  LaptopOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import interviewService from "../../../../../Service/interview.service";

const { Text, Paragraph } = Typography;

interface ReviewSummaryContentsProps {
  formData: JobPostFormData;
  stepNumber: number;
}

const basicDetailsSummaryContent = (formData: JobPostFormData) => {
  if (!formData.step1) return <Text type="secondary">No data available</Text>;
  const {
    title,
    position,
    numberOfOpenings,
    noticePeriod,
    ctc,
    currency,
    currencyCode,
    endDate,
    description,
  } = formData.step1;

  const items = [];

  if (title) {
    items.push({
      key: "title",
      icon: <FileTextOutlined />,
      label: "Title",
      value: <Text>{title}</Text>,
    });
  }

  if (position) {
    items.push({
      key: "position",
      icon: <UserOutlined />,
      label: "Position",
      value: <Text>{position}</Text>,
    });
  }

  if (noticePeriod !== null && noticePeriod !== undefined) {
    items.push({
      key: "noticePeriod",
      icon: <CalendarOutlined />,
      label: "Candidate Availability/ Notice period",
      value: (
        <Text>
          {noticePeriod === "0" ? "Immediate" : `${noticePeriod} days`}
        </Text>
      ),
    });
  }

  if (ctc) {
    const currencyToDisplay = currency || currencyCode || "";
    const ctcDisplay = currencyToDisplay
      ? `${ctc} ${currencyToDisplay}`
      : String(ctc);
    items.push({
      key: "ctc",
      icon: <DollarOutlined />,
      label: "CTC",
      value: <Text>{ctcDisplay}</Text>,
    });
  }

  if (numberOfOpenings !== null && numberOfOpenings !== undefined) {
    items.push({
      key: "numberOfOpenings",
      icon: <UsergroupAddOutlined />,
      label: "Number of Openings",
      value: <Text>{numberOfOpenings}</Text>,
    });
  }

  if (endDate !== null && endDate !== undefined) {
    items.push({
      key: "endDate",
      icon: <CalendarOutlined />,
      label: "End Date",
      value: <Text>{dayjs(endDate).format("DD-MMM-YYYY")}</Text>,
    });
  }

  return (
    <div className="review-summary-card-content">
      <List
        itemLayout="horizontal"
        size="small"
        dataSource={items}
        bordered={false}
        split={false}
        style={{ padding: 0 }}
        renderItem={(item, index) => (
          <List.Item key={index}>
            <List.Item.Meta
              description={
                <Text>
                  <span style={{ fontWeight: "bold" }}>
                    {item.icon} &nbsp; {item.label} :
                  </span>
                  &nbsp; {item.value}
                </Text>
              }
            />
          </List.Item>
        )}
      />

      {description && (
        <div className="review-summary-card-description">
          <Paragraph>{description}</Paragraph>
        </div>
      )}

      <Divider style={{ margin: "8px 0" }} />

      <div>
        <Text strong>Candidate Notifications</Text>
        <List
          itemLayout="horizontal"
          size="small"
          dataSource={[
            {
              key: "email",
              icon: <MailOutlined />,
              label: "Email",
              enabled: formData.step1.notifications?.email ?? true,
            },
            {
              key: "whatsapp",
              icon: <WhatsAppOutlined />,
              label: "WhatsApp",
              enabled: formData.step1.notifications?.whatsapp ?? true,
            },
          ]}
          bordered={false}
          split={false}
          style={{ padding: 0 }}
          renderItem={(item) => (
            <List.Item key={item.key}>
              <List.Item.Meta
                description={
                  <Text>
                    <span style={{ fontWeight: "bold" }}>
                      {item.icon} &nbsp; {item.label} :
                    </span>
                    &nbsp;
                    <Tag color={item.enabled ? "success" : "default"}>
                      {item.enabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </div>
  );
};

const requirementsSummaryContent = (formData: JobPostFormData) => {
  if (!formData.step2)
    return <Text type="secondary">No requirements added</Text>;

  const allRequirements = [
    ...(formData.step2.requirements || []),
    ...(formData.step2.additionalRequirements || []),
  ];

  if (allRequirements.length === 0) {
    return <Text type="secondary">No requirements added</Text>;
  }

  const mustMatchRequirements = allRequirements.filter(
    (req: any) => req.mustMatch === true
  );
  const otherRequirements = allRequirements.filter(
    (req: any) => req.mustMatch !== true
  );

  return (
    <div className="review-summary-card-content">
      {mustMatchRequirements.length > 0 && (
        <List
          size="small"
          header={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <Text strong>Must Match Requirements</Text>
              <Tag color="default" icon={<CheckOutlined />}>
                {mustMatchRequirements.length}
              </Tag>
            </div>
          }
          bordered={false}
          dataSource={mustMatchRequirements}
          renderItem={(item: any) => (
            <List.Item key={item._id}>{item.description}</List.Item>
          )}
        />
      )}

      {otherRequirements.length > 0 && (
        <List
          size="small"
          header={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <Text strong>Other Requirements</Text>
              <Tag color="default" icon={<QuestionCircleOutlined />}>
                {otherRequirements.length}
              </Tag>
            </div>
          }
          bordered={false}
          dataSource={otherRequirements}
          renderItem={(item: any) => (
            <List.Item key={item._id}>{item.description}</List.Item>
          )}
        />
      )}
    </div>
  );
};

const interviewRoundsSummaryContent = (
  formData: JobPostFormData,
  roundTitles: Record<string, string>,
  loadingTitles: boolean
) => {
  if (
    !formData.step3 ||
    !formData.step3.interviewRounds ||
    formData.step3.interviewRounds.length === 0
  ) {
    return <Text type="secondary">No interview rounds added</Text>;
  }

  if (loadingTitles) {
    return <Spin size="small" />;
  }
  return (
    <div className="review-summary-card-content">
      <List
        size="small"
        dataSource={formData.step3.interviewRounds}
        renderItem={(round: any) => {
          const roundId = round.interviewRoundId || round._id || "";
          const roundTitle =
            roundTitles[roundId] ||
            round.title ||
            round.roundName ||
            (roundId === "profile-match"
              ? "Profile Matching"
              : roundId === "job-alignment"
              ? "Job Alignment"
              : roundId === "overall-assessment"
              ? "Overall Assessment"
              : "Untitled Round");
          return (
            <List.Item>
              <Text>{roundTitle}</Text>
            </List.Item>
          );
        }}
      />
    </div>
  );
};

const jobAlignmentSummaryContent = (formData: JobPostFormData) => {
  if (!formData.step4) {
    return <Text type="secondary">No Job Alignment configuration</Text>;
  }

  const jobAlignment = formData.step4.jobAlignment;
  const screeningQuestions = formData.step4.manualScreeningQuestions || [];

  if (!jobAlignment && screeningQuestions.length === 0) {
    return <Text type="secondary">No Job Alignment configuration</Text>;
  }

  const isAIGeneratedValue = jobAlignment?.isAIGenerated ?? "";
  const isAIGenerated =
    isAIGeneratedValue === true ||
    (typeof isAIGeneratedValue === "string" &&
      isAIGeneratedValue.toLowerCase() === "true");

  return (
    <div className="review-summary-card-content">
      <Text strong>Configurations:</Text>
      <Descriptions column={1} size="small" bordered={false}>
        <Descriptions.Item label="AI Generated">
          <Tag color={isAIGenerated ? "blue" : "default"}>
            {isAIGenerated ? "Yes" : "No"}
          </Tag>
        </Descriptions.Item>
        {/* Pass Percentage display - Commented out for this release, to be considered in next release
              {jobAlignment.passPercentage !== undefined && jobAlignment.passPercentage !== null && (
                <Descriptions.Item label="Pass Percentage">
                  <Text>{jobAlignment.passPercentage}%</Text>
                </Descriptions.Item>
              )}
              */}
        {/* Must Qualify display - Commented out for this release, to be considered in next release
              <Descriptions.Item label="Must Qualify">
                <Tag color={jobAlignment.isMustQualify === true ? 'success' : 'default'}>
                  {jobAlignment.isMustQualify === true ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              */}
      </Descriptions>

      <Divider style={{ margin: 0 }} />

      {screeningQuestions.length > 0 && (
        <Text strong>Custom Screening Questions:</Text>
      )}

      {screeningQuestions.length > 0 && (
        <List
          dataSource={screeningQuestions}
          renderItem={(question: any, index: number) => (
            <List.Item>
              <Space>
                <QuestionCircleOutlined />
                <Text>
                  {question.description ||
                    question.title ||
                    `Question ${index + 1}`}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}

      {!jobAlignment && screeningQuestions.length === 0 && (
        <Text type="secondary">
          No custom screening questions added (optional)
        </Text>
      )}
    </div>
  );
};

const postAssessmentSummaryContent = (formData: JobPostFormData) => {
  if (!formData.step5) return <Text type="secondary">No data available</Text>;
  return (
    <div className="review-summary-card-content">
      <div>
        <Text strong>
          {formData.step5.onlineInterview ? (
            <LaptopOutlined />
          ) : (
            <EnvironmentOutlined />
          )}{" "}
          &nbsp; Interview Mode :
        </Text>
        &nbsp;
        <Tag color={formData.step5.onlineInterview ? "blue" : "green"}>
          {formData.step5.onlineInterview ? "Online" : "Team Will Contact"}
        </Tag>
      </div>
      {formData.step5.onlineInterview &&
        formData.step5.interviewers &&
        formData.step5.interviewers.length > 0 && (
          <Divider style={{ margin: 0 }} />
        )}
      {formData.step5.onlineInterview &&
        formData.step5.interviewers &&
        formData.step5.interviewers.length > 0 && (
          <div>
            <Text strong>
              <TeamOutlined />
              &nbsp; Interviewers : &nbsp;
              <Text type="secondary">
                {formData.step5.interviewers.length} selected
              </Text>
            </Text>
          </div>
        )}

      {formData.step5.onlineInterview &&
        formData.step5.interviewGuests &&
        formData.step5.interviewGuests.length > 0 && (
          <Divider style={{ margin: 0 }} />
        )}

      {formData.step5.onlineInterview &&
        formData.step5.interviewGuests &&
        formData.step5.interviewGuests.length > 0 && (
          <div>
            <Text strong>
              <UserOutlined />
              &nbsp; External Guests : &nbsp;
              <Text type="secondary">
                {formData.step5.interviewGuests.length} guest(s)
              </Text>
            </Text>
          </div>
        )}
    </div>
  );
};


const ReviewSummaryContents: React.FC<ReviewSummaryContentsProps> = ({
  formData,
  stepNumber,
}) => {
  const [roundTitles, setRoundTitles] = useState<Record<string, string>>({});
  const [loadingTitles, setLoadingTitles] = useState(false);

  useEffect(() => {
    if (
      stepNumber === 3 &&
      formData.step3 &&
      formData.step3.interviewRounds &&
      formData.step3.interviewRounds.length > 0
    ) {
      const fetchRoundTitles = async () => {
        setLoadingTitles(true);
        const titlesMap: Record<string, string> = {};

        try {
          // Fetch only generic rounds (profile-match, overall-assessment, job-alignment)
          // This avoids processing custom rounds that aren't needed for title lookup
          const allRounds =
            await interviewService.getGenericRoundsOnly(
              false
            );

          // Map round IDs to titles
          for (const round of formData.step3.interviewRounds) {
            const roundId = round.interviewRoundId || (round as any)._id;

            if (!roundId) {
              continue;
            }

            // Handle special cases (same logic as Step 3's refreshRoundFromCache)
            if (
              roundId === "profile-match" ||
              roundId === "overall-assessment"
            ) {
              const apiRound = allRounds.find((r: any) => {
                if (roundId === "profile-match") {
                  // Profile Matching: check for isGeneric and category "Profile Match"
                  const isGeneric = r.isGeneric;
                  const categories = r.category || [];
                  const hasProfileMatchCategory = categories.some(
                    (cat: string) =>
                      cat.toLowerCase().replaceAll(/\s+/g, "") ===
                      "profilematch"
                  );
                  return isGeneric && hasProfileMatchCategory;
                } else if (roundId === "overall-assessment") {
                  // Overall Assessment: check for isGeneric and title "Overall Assessment"
                  const isGeneric = r.isGeneric;
                  const titleMatch =
                    r.title?.toLowerCase().trim() === "overall assessment";
                  return isGeneric && titleMatch;
                }
                return false;
              });

              if (apiRound) {
                const apiRoundAny = apiRound as any;
                let defaultTitle = "Untitled Round";
                if (roundId === "profile-match") {
                  defaultTitle = "Profile Matching";
                } else if (roundId === "job-alignment") {
                  defaultTitle = "Job Alignment";
                } else if (roundId === "overall-assessment") {
                  defaultTitle = "Overall Assessment";
                }
                titlesMap[roundId] =
                  apiRoundAny.roundName || apiRound.title || defaultTitle;
              } else {
                // Fallback to hardcoded names
                if (roundId === "profile-match") {
                  titlesMap[roundId] = "Profile Matching";
                } else if (roundId === "job-alignment") {
                  titlesMap[roundId] = "Job Alignment";
                } else if (roundId === "overall-assessment") {
                  titlesMap[roundId] = "Overall Assessment";
                }
              }
            } else {
              // For custom rounds with real IDs, find in fetched rounds first
              const foundRound = allRounds.find((r: any) => r._id === roundId);
              if (foundRound) {
                const foundRoundAny = foundRound as any;
                titlesMap[roundId] =
                  foundRoundAny.roundName ||
                  foundRound.title ||
                  "Untitled Round";
              } else {
                // Try fetching individual round
                try {
                  const fullRound =
                    await interviewService.getInterviewRoundById(roundId);
                  if (fullRound) {
                    const fullRoundAny = fullRound as any;
                    titlesMap[roundId] =
                      fullRoundAny.roundName ||
                      fullRound.title ||
                      "Untitled Round";
                  } else {
                    titlesMap[roundId] = "Untitled Round";
                  }
                } catch (error) {
                  console.error(`Error fetching round ${roundId}:`, error);
                  titlesMap[roundId] = "Untitled Round";
                }
              }
            }
          }

          setRoundTitles(titlesMap);
        } catch (error) {
          console.error("Error fetching round titles:", error);
        } finally {
          setLoadingTitles(false);
        }
      };

      fetchRoundTitles();
    }
  }, [stepNumber, formData.step3]);

  switch (stepNumber) {
    case 1:
      return basicDetailsSummaryContent(formData);
    case 2:
      return requirementsSummaryContent(formData);
    case 3:
      return interviewRoundsSummaryContent(
        formData,
        roundTitles,
        loadingTitles
      );
    case 4:
      return jobAlignmentSummaryContent(formData);
    case 5:
      return postAssessmentSummaryContent(formData);
    default:
      return (
        <div>
          <Text type="secondary">Invalid step number</Text>
        </div>
      );
  }
};

export default ReviewSummaryContents;
